import { useState } from 'react'
import { Panel, Button, Chip, Modal, FormRow, Textarea, Checkbox, ConfirmDialog, EmptyState, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, patch, del } from '../lib/api'
import { dateShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from './Loading'

export type ClientDocument = {
  id: string; subscriber_id: string | null; title: string; description: string | null; body: string | null
  file_name: string | null; file_type: string | null; file_size: number | null; pinned: boolean
  created_at: string; updated_at: string; created_by_name: string | null
}

const MAX_BYTES = 5 * 1024 * 1024
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.docx,.xlsx'
// Browsers sometimes leave a file's type blank (often .csv and .docx on Windows). Fill it from the name.
const BY_EXT: Record<string, string> = {
  pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', txt: 'text/plain', csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export const fileSize = (n: number | null | undefined) => {
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''))
    r.onerror = () => reject(new Error('Could not read that file'))
    r.readAsDataURL(file)
  })
}

/**
 * The documents and guides an admin shares with one client, or (clientId null)
 * with every client. Add, edit, open and remove.
 */
export default function ClientDocuments({ clientId, clientName }: { clientId: string | null; clientName?: string }) {
  const toast = useToast()
  const { data, error, reload } = useApi<{ documents: ClientDocument[] }>(`/api/documents?client=${clientId ?? 'shared'}`)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<ClientDocument | null>(null)
  const [removing, setRemoving] = useState<ClientDocument | null>(null)

  const remove = async (d: ClientDocument) => {
    setRemoving(null)
    try { await del(`/api/documents/${d.id}`); toast(`Removed ${d.title}`, 'pos'); reload() }
    catch (e) { toast((e as Error).message, 'neg') }
  }

  const heading = clientId ? `Documents for ${clientName || 'this client'}` : 'Shared with every client'
  return (
    <Panel>
      <div className="panel-header" style={{ flexWrap: 'wrap', gap: 'var(--s-3)' }}>
        <h2 className="panel-title">{heading}</h2>
        <Button variant="secondary" onClick={() => setAdding(true)}>Add</Button>
      </div>
      {error && <ErrorBlock error={error} />}
      {!error && !data && <LoadingBlock rows={3} />}
      {data && data.documents.length === 0 && (
        <EmptyState title="Nothing here yet"
                    body={clientId ? `Add a contract, a price sheet or notes just for ${clientName || 'this client'}.` : 'Add a price sheet, a guide to how leads work, or onboarding steps. Every client sees these in their portal.'} />
      )}
      {data && data.documents.map((d) => (
        <div key={d.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-3)', justifyContent: 'space-between', alignItems: 'flex-start', padding: 'var(--s-4) 0', borderTop: '1px solid var(--border)' }}>
          <div style={{ minWidth: 0, flex: '1 1 220px' }}>
            <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ overflowWrap: 'anywhere' }}>{d.title}</strong>
              {d.pinned && <Chip tone="accent">Pinned</Chip>}
            </div>
            {d.description && <div className="muted" style={{ overflowWrap: 'anywhere' }}>{d.description}</div>}
            <div className="muted" style={{ overflowWrap: 'anywhere' }}>
              {d.file_name ? `${d.file_name}, ${fileSize(d.file_size)}` : 'Guide'}
              {d.file_name && d.body ? ', with a guide' : ''}
              {`, added ${dateShort(d.created_at)}`}{d.created_by_name ? ` by ${d.created_by_name}` : ''}
            </div>
          </div>
          <div className="row-tight" style={{ flexWrap: 'wrap' }}>
            {d.file_name && <a className="btn btn-ghost" href={`/api/documents/${d.id}/file`} target="_blank" rel="noopener noreferrer">Open</a>}
            <Button variant="ghost" onClick={() => setEditing(d)}>Edit</Button>
            <Button variant="ghost" onClick={() => setRemoving(d)}>Remove</Button>
          </div>
        </div>
      ))}

      {adding && <AddModal clientId={clientId} heading={heading} onClose={() => setAdding(false)} onSaved={reload} />}
      {editing && <EditModal doc={editing} onClose={() => setEditing(null)} onSaved={reload} />}
      {removing && (
        <ConfirmDialog title={`Remove ${removing.title}?`} tone="neg" confirmLabel="Remove"
                       body={removing.subscriber_id ? 'The client will no longer see it. This cannot be undone.' : 'No client will see it any more. This cannot be undone.'}
                       onConfirm={() => remove(removing)} onCancel={() => setRemoving(null)} />
      )}
    </Panel>
  )
}

function AddModal({ clientId, heading, onClose, onSaved }: { clientId: string | null; heading: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [pinned, setPinned] = useState(false)
  const [busy, setBusy] = useState(false)

  const pick = (f: File | null) => {
    if (f && f.size > MAX_BYTES) { toast('Files can be up to 5 MB', 'neg'); return }
    setFile(f)
    if (f && !title.trim()) setTitle(f.name.replace(/\.[^.]+$/, '').slice(0, 200))
  }

  const save = async () => {
    if (!title.trim()) return toast('Give it a title', 'neg')
    if (!file && !body.trim()) return toast('Add a file or write a guide', 'neg')
    setBusy(true)
    try {
      let upload: { name: string; type: string; data_base64: string } | undefined
      if (file) {
        const ext = file.name.split('.').pop()?.toLowerCase() || ''
        upload = { name: file.name, type: file.type || BY_EXT[ext] || '', data_base64: await readBase64(file) }
      }
      await post('/api/documents', {
        subscriber_id: clientId, title: title.trim(), description: description.trim() || null, body: body.trim() || null, pinned, file: upload,
      })
      toast('Added', 'pos'); onSaved(); onClose()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  return (
    <Modal title={`Add to ${heading.charAt(0).toLowerCase()}${heading.slice(1)}`} onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? 'Adding...' : 'Add'}</Button></>}>
      <div className="stack-tight">
        <FormRow label="File (optional)" htmlFor="doc-file" help="PDF, image, Word, Excel, CSV or text. Up to 5 MB.">
          <input id="doc-file" type="file" accept={ACCEPT} onChange={(e) => pick(e.target.files?.[0] || null)} style={{ maxWidth: '100%' }} />
        </FormRow>
        <FormRow label="Title" htmlFor="doc-title" required>
          <input id="doc-title" className="field" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </FormRow>
        <FormRow label="Short description (optional)" htmlFor="doc-desc">
          <input id="doc-desc" className="field" value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
        </FormRow>
        <FormRow label="Guide (optional)" htmlFor="doc-body" help="Plain text. Leave a blank line between paragraphs.">
          <Textarea id="doc-body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </FormRow>
        <Checkbox checked={pinned} onChange={(e) => setPinned(e.target.checked)} label="Pin to the top" />
      </div>
    </Modal>
  )
}

function EditModal({ doc, onClose, onSaved }: { doc: ClientDocument; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [title, setTitle] = useState(doc.title)
  const [description, setDescription] = useState(doc.description || '')
  const [body, setBody] = useState(doc.body || '')
  const [pinned, setPinned] = useState(doc.pinned)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!title.trim()) return toast('Give it a title', 'neg')
    if (!doc.file_name && !body.trim()) return toast('A document without a file needs a guide', 'neg')
    setBusy(true)
    try {
      await patch(`/api/documents/${doc.id}`, { title: title.trim(), description: description.trim() || null, body: body.trim() || null, pinned })
      toast('Saved', 'pos'); onSaved(); onClose()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  return (
    <Modal title={`Edit ${doc.title}`} onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>Save</Button></>}>
      <div className="stack-tight">
        {doc.file_name && <p className="muted" style={{ margin: 0, overflowWrap: 'anywhere' }}>File: {doc.file_name}. To change the file, remove this and add it again.</p>}
        <FormRow label="Title" htmlFor="doc-e-title" required>
          <input id="doc-e-title" className="field" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </FormRow>
        <FormRow label="Short description (optional)" htmlFor="doc-e-desc">
          <input id="doc-e-desc" className="field" value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
        </FormRow>
        <FormRow label={doc.file_name ? 'Guide (optional)' : 'Guide'} htmlFor="doc-e-body" help="Plain text. Leave a blank line between paragraphs.">
          <Textarea id="doc-e-body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </FormRow>
        <Checkbox checked={pinned} onChange={(e) => setPinned(e.target.checked)} label="Pin to the top" />
      </div>
    </Modal>
  )
}
